
import React, { useEffect, useState } from "react";
import { useLanguage } from "../../Translations/LenguageContext";
import { useNavigate } from "react-router-dom";
import { PiMagnifyingGlass } from "react-icons/pi";
import ContextsLoader from "../../Loaders/ContextsLoader";
import FormatDateTime from "../../Functions/FormatDate";
import { TbInfoSquare } from "react-icons/tb";
import { MdOutlineChecklist } from "react-icons/md";
import Spinner from "../../Components/Spinner";
import ErrorMessage from "../../Components/ErrorMessage";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const GeoContext = ({
    account_id,
    org_id,
    geo_id,
    selectedContexts,
    setSelectedContexts,
    saveContextGeo,
    loadingSave,
    setLoadingSave,
    errorMessage,
    setErrorMessage
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();

    const [loadingContext, setLoadingContext] = useState(false);
    const [contextData, setContextData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const navigateBack = () => {
        setLoadingSave(false)
        setErrorMessage({ message: "", display: false });
        navigate(`/console/organizations/${org_id}/geo/${geo_id}/language`);
    };

    const fetchContextData = async () => {
        setLoadingContext(true);

        try {
            const response = await fetch(
                `${GEN_BACKEND_LINK}/get_contexts`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        account_id,
                        org_id,
                        geo_id,
                    }),
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setContextData(result.data);
        } catch (err) {
            console.error("Error fetching GEO optimization:", err);
        } finally {
            setLoadingContext(false);
        }
    };

    const navigateNewContext = () => {
        navigate(`/console/organizations/${org_id}/context`);
    };

    const filteredContexts = contextData.filter((data) => {
        const search = searchTerm.toLowerCase().trim();

        if (!search) return true;

        return (
            String(data.id ?? "").toLowerCase().includes(search) ||
            String(data.name ?? "").toLowerCase().includes(search) ||
            String(data.description ?? "").toLowerCase().includes(search) ||
            String(data.created_at ?? "").toLowerCase().includes(search) ||
            String(FormatDateTime(data.created_at) ?? "")
                .toLowerCase()
                .includes(search)
        );
    });

    const selectedContextsWithoutSources = contextData.filter(
        (context) =>
            selectedContexts.includes(context.id) &&
            Number(context.sources_count) === 0
    );

    const handleContextClick = (contextId) => {
        setSelectedContexts((prev) =>
            prev.includes(contextId)
                ? prev.filter((id) => id !== contextId)
                : [...prev, contextId]
        );
    };

    const allFilteredSelected =
        filteredContexts.length > 0 &&
        filteredContexts.every((context) =>
            selectedContexts.includes(context.id)
        );

    const handleSelectAll = () => {
        if (allFilteredSelected) {
            const filteredIds = new Set(
                filteredContexts.map((context) => context.id)
            );

            setSelectedContexts((prev) =>
                prev.filter((id) => !filteredIds.has(id))
            );
        } else {
            setSelectedContexts((prev) => [
                ...new Set([
                    ...prev,
                    ...filteredContexts.map((context) => context.id),
                ]),
            ]);
        }
    };

    useEffect(() => {
        if (account_id && org_id && geo_id) {
            fetchContextData();
        }
    }, [account_id, org_id, geo_id]);

    return (
        <div className="geo_context_cont">
            <p className="add_new_source_cont_title fade-in">
                {t.geo.geo_context_title}
            </p>

            <p className="add_new_source_cont_desc fade-in">
                {t.geo.geo_context_description}
            </p>

            {loadingContext ? (
                <ContextsLoader />
            ) : (
                contextData.length > 0 ? (
                    <div className="geo_context_choise_container">
                        <div className="geo_context_search_container fade-in">
                            <input
                                className="geo_context_search_input"
                                placeholder={t.geo.geo_filter_placeholder}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <i>
                                <PiMagnifyingGlass />
                            </i>
                        </div>

                        <div className="geo_context_choise_cont">
                            {filteredContexts.length > 0 ? (
                                <div className="geo_context_choise_cont_container">
                                    <div className="geo_context_choise_cont_header">
                                        <button
                                            type="button"
                                            className="source_website_pages_list_header_btn"
                                            onClick={handleSelectAll}
                                        >
                                            <MdOutlineChecklist />{" "}
                                            {allFilteredSelected
                                                ? t.geo.deselect_all
                                                : t.geo.select_all}
                                        </button>

                                        <p>
                                            {filteredContexts.length}{" "}
                                            {filteredContexts.length > 1
                                                ? t.geo.items
                                                : t.geo.item}
                                        </p>
                                    </div>

                                    <div className="geo_context_choise_cont_body">
                                        {filteredContexts.map((data) => {
                                            const isSelected =
                                                selectedContexts.includes(data.id);

                                            const hasNoSources =
                                                Number(data.sources_count) === 0;


                                            return (
                                                <div
                                                    className={`geo_context_choise fade-in ${isSelected
                                                        ? "geo_context_choise_selected"
                                                        : ""
                                                        }`}
                                                    key={data.id}
                                                    onClick={() =>
                                                        handleContextClick(data.id)
                                                    }
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-pressed={isSelected}
                                                >
                                                    <p>
                                                        <i>
                                                            <TbInfoSquare />
                                                        </i>

                                                        {data.name}

                                                        {hasNoSources ? (
                                                            <span className="source_count no_sources">
                                                                {t.geo.no_sources}
                                                            </span>
                                                        ) : (
                                                            <span className="source_count">
                                                                {data.sources_count}{" "}
                                                                {Number(
                                                                    data.sources_count
                                                                ) > 1
                                                                    ? t.geo.sources
                                                                    : t.geo.source}
                                                            </span>
                                                        )}
                                                    </p>

                                                    <span>
                                                        {FormatDateTime(
                                                            data.created_at
                                                        )}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {selectedContextsWithoutSources.length > 0 && (
                                        <p className="selected_contexts_warning fade-in" dangerouslySetInnerHTML={{ __html: t.geo.warning_no_sources }}>
                                        </p>
                                    )}
                                </div>
                            ) : (
                                <div className="organizations_cont_no_items_mex fade-in">
                                    <p>{t.no_data_found_by_search}</p>
                                </div>
                            )}
                        </div>

                        <p className="warning_source_message fade-in">
                            {t.geo.need_context}
                            <button
                                type="button"
                                className="create_new_cntx_btn"
                                onClick={navigateNewContext}
                            >
                                {t.geo.click_here}
                            </button>
                        </p>

                        <div className="modal_footer_err_cont">
                            <ErrorMessage err={errorMessage} />
                        </div>

                        <div className="open_frame_body_footer">
                            <button
                                type="button"
                                className="modale_save_btn fade-in"
                                onClick={navigateBack}
                            >
                                <i className="fa-solid fa-chevron-left"></i>
                                {t.back}
                            </button>
                            {(selectedContexts.length > 0 && selectedContextsWithoutSources.length === 0) && (
                                <button
                                    type="button"
                                    className="modale_save_btn fade-in"
                                    onClick={saveContextGeo}
                                >
                                    {loadingSave ? (
                                        <Spinner loading={loadingSave} />
                                    ) : (
                                        <>
                                            {t.next}
                                            <i className="fa-solid fa-chevron-right"></i>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="geo_context_choise_container">
                        <div className="organizations_cont_no_items_mex fade-in">
                            <p>{t.no_data_found}</p>
                        </div>
                        <p className="warning_source_message fade-in">
                            {t.geo.need_context}
                            <button
                                type="button"
                                className="create_new_cntx_btn"
                                onClick={navigateNewContext}
                            >
                                {t.geo.click_here}
                            </button>
                        </p>
                    </div>
                )
            )}
        </div>
    );
};

export default GeoContext;
